import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/organisms/Header';
import Footer from '../components/organisms/Footer';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import Input from '../components/atoms/Input';
import SessionManager from '../utils/sessionManager';
import { API_BASE_URL } from '../config';

// Build headers with JWT Authorization from SessionManager
const buildAuthHeaders = (extra = {}) => {
  let session = SessionManager.getCurrentSession();
  if (!session || !session.access_token) {
    session = SessionManager.getSession('candidate') || SessionManager.getSession('hr');
  }
  const token = session?.access_token;
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const HrProfileSettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    contact_name: '',
    company_name: '',
    company_website: '',
    phone_country_code: '+91',
    phone_number: ''
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Get HR email
  const getHrEmail = () => {
    SessionManager.migrateLegacySession();
    const hrSession = SessionManager.getSession('hr');
    return hrSession?.email || hrSession?.profile?.email;
  };

  const hrEmail = getHrEmail();

  // Check HR authentication
  useEffect(() => {
    const isLoggedInAsHr = SessionManager.isLoggedIn('hr');
    const currentSession = SessionManager.getCurrentSession();
    
    if (!isLoggedInAsHr || (currentSession && currentSession.role !== 'hr')) {
      navigate('/login');
      return;
    }
  }, [navigate]);

  // Fetch profile
  useEffect(() => {
    const fetchProfile = async () => {
      if (!hrEmail) {
        setErrorMessage('Unable to identify user. Please log in again.');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/hr/profile?hr_email=${encodeURIComponent(hrEmail)}`, { headers: buildAuthHeaders() });
        if (!response.ok) throw new Error('Failed to fetch profile');
        
        const data = await response.json();
        setProfile(data);
        setFormData({
          contact_name: data.contact_name || '',
          company_name: data.company_name || '',
          company_website: data.company_website || '',
          phone_country_code: data.phone_country_code || '+91',
          phone_number: data.phone_number || ''
        });
      } catch (error) {
        console.error('Error fetching profile:', error);
        setErrorMessage('Failed to load profile. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [hrEmail]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.contact_name.trim() || !formData.company_name.trim()) {
      setErrorMessage('Contact name and company name are required');
      return;
    }

    setSaving(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/hr/profile?hr_email=${encodeURIComponent(hrEmail)}`, {
        method: 'PUT',
        headers: { ...buildAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to update profile');
      
      const data = await response.json();
      setProfile(data.profile);
      
      // Update session with new company name
      const hrSession = SessionManager.getSession('hr');
      if (hrSession) {
        hrSession.company = data.profile.company_name;
        if (hrSession.profile) {
          hrSession.profile.company_name = data.profile.company_name;
          hrSession.profile.contact_name = data.profile.contact_name;
        }
        SessionManager.setSession('hr', hrSession);
      }
      
      setSuccessMessage('Profile updated successfully!');
      
      // Redirect back to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/hr/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrorMessage('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f7fb]">
        <Header variant="dashboard" />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-gray-600">Loading profile...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f7fb]">
      <Header variant="dashboard" />
      
      <main className="flex-1 px-4 py-6 sm:px-6 lg:px-10">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => navigate('/hr/dashboard')}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
            >
              <Icon name="arrow_back" size={20} className="mr-2" />
              Back to Dashboard
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Profile Settings</h1>
            <p className="text-gray-600 mt-1">Update your company and contact information</p>
          </div>

          {/* Profile Form */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Success Message */}
              {successMessage && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
                  <Icon name="check_circle" size={20} className="text-green-600 mr-3" />
                  <p className="text-green-800">{successMessage}</p>
                </div>
              )}

              {/* Error Message */}
              {errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
                  <Icon name="error" size={20} className="text-red-600 mr-3" />
                  <p className="text-red-800">{errorMessage}</p>
                </div>
              )}

              {/* Contact Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Name *
                    </label>
                    <Input
                      value={formData.contact_name}
                      onChange={(e) => handleChange('contact_name', e.target.value)}
                      placeholder="Your full name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <Input
                      value={profile?.email || ''}
                      disabled
                      className="bg-gray-50 cursor-not-allowed"
                    />
                    <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={formData.phone_country_code}
                        onChange={(e) => handleChange('phone_country_code', e.target.value)}
                        className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="+91">+91</option>
                        <option value="+1">+1</option>
                        <option value="+44">+44</option>
                        <option value="+65">+65</option>
                      </select>
                      <Input
                        value={formData.phone_number}
                        onChange={(e) => handleChange('phone_number', e.target.value)}
                        placeholder="Phone number"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Company Information */}
              <div className="pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name *
                    </label>
                    <Input
                      value={formData.company_name}
                      onChange={(e) => handleChange('company_name', e.target.value)}
                      placeholder="Your company name"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      This will be displayed on job postings and communications
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Website
                    </label>
                    <Input
                      value={formData.company_website}
                      onChange={(e) => handleChange('company_website', e.target.value)}
                      placeholder="https://yourcompany.com"
                      type="url"
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-6 border-t border-gray-200 flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/hr/dashboard')}
                  disabled={saving}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={saving}
                  className="flex-1"
                >
                  {saving ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Icon name="save" size={20} className="mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HrProfileSettingsPage;
